import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight } from "@phosphor-icons/react";
import client from "../../lib/api";


/* ─────────────────────  PRICING TRUST STRIP (below hero)  ───────────────────── */

/* -----------------------------------------------------------------
 * WhyUsSection — the trust-and-transparency proof section.
 * Positions FreightPilotAI as the founder-run, no-card, live-Zoom
 * alternative to the anonymous "AI dispatch" pattern many small
 * fleets have been burned by. Ships LOWER in the page (after the
 * positive value story) so it reads as differentiation-with-proof,
 * not defensive opener.
 * ----------------------------------------------------------------- */
export function AntiScamSection() {
  const rows = [
    {
      label: "Who's behind it",
      others: "Faceless 'team' with stock photos, offshore support",
      us: (
        <>
          One founder: <b>Michael Guley</b> · Sioux Falls, SD ·
          <br /> Direct cell: <span className="text-white">(605) 679-7303</span>
        </>
      ),
    },
    {
      label: "Business registration",
      others: "Delaware LLC through nominee service, no traceable owner",
      us: (
        <>
          <b>Freight Pilot AI LLC</b> · SD Sec. of State filed · IRS EIN issued ·
          USPTO trademark filed
        </>
      ),
    },
    {
      label: "How you sign up",
      others: "A 'free trial' that's hard to cancel, with surprise charges buried in the fine print",
      us: (
        <>
          <b>Card on file, $0 today.</b> A clear 7-day trial, then the plan you picked.
          Cancel in one tap from Billing before day 8 and you're never charged.
        </>
      ),
    },
    {
      label: "Setup",
      others: "Vague onboarding video + 'AI does everything' — until it breaks",
      us: (
        <>
          <b>I personally set it up for you</b> in a live 30-min Zoom while you
          watch. Every field. Your rules.
        </>
      ),
    },
    {
      label: "Your data",
      others: "Locked behind paywall, no export, held hostage if you cancel",
      us: (
        <>
          <b>Export to CSV, QuickBooks, or JSON anytime.</b> Cancel with one
          click. Your data leaves with you.
        </>
      ),
    },
    {
      label: "If it doesn't work",
      others: "Support tickets that go unanswered; cancellation buried under paywalls",
      us: (
        <>
          <b>$100 out of my pocket.</b> If you haven't saved 5+ hours in 14
          days, I write you a personal check. My word.
        </>
      ),
    },
  ];

  return (
    <section
      id="why-us"
      data-testid="why-us-section"
      className="relative border-b border-white/10 py-16 sm:py-20"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-8">
        <div className="max-w-3xl mb-10">
          <div className="text-[10px] uppercase tracking-widest text-[#FF9764] font-semibold mb-3">
            What makes us different
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.05] tracking-tight mb-4">
            Every promise on this page is <span className="text-[#FF5A1F]">independently verifiable</span>.
          </h2>
          <p className="text-neutral-300 text-base sm:text-lg leading-relaxed">
            After 50+ calls with fleet owners, I kept hearing the same worries about "AI dispatch" — faceless teams, credit-card traps, and platforms that hold your data hostage. FreightPilotAI is my answer: <span className="text-white font-medium">founder-run, a 7-day free trial you can cancel in one click, and every workflow set up live on Zoom.</span> Here's exactly how we stack up against the industry norm.
          </p>
        </div>

        <div className="rounded-2xl border border-white/10 overflow-hidden bg-black/40">
          {/* Table header */}
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1.6fr)] text-[10px] uppercase tracking-widest bg-white/5 border-b border-white/10">
            <div className="p-3 sm:p-4 text-neutral-400 hidden md:block">Comparison</div>
            <div className="p-3 sm:p-4 text-neutral-300 font-semibold border-t md:border-t-0 md:border-l border-white/10">
              Typical "AI dispatch" company
            </div>
            <div className="p-3 sm:p-4 text-emerald-300 font-semibold border-t md:border-t-0 md:border-l border-white/10">
              FreightPilotAI
            </div>
          </div>
          {rows.map((r, i) => (
            <div
              key={i}
              className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1.6fr)] border-b border-white/10 last:border-b-0"
            >
              <div className="p-4 sm:p-5 text-[11px] uppercase tracking-widest text-neutral-400 md:font-semibold">
                {r.label}
              </div>
              <div className="p-4 sm:p-5 text-sm text-neutral-300 border-t md:border-t-0 md:border-l border-white/10 leading-relaxed">
                {r.others}
              </div>
              <div className="p-4 sm:p-5 text-sm text-white border-t md:border-t-0 md:border-l border-white/10 bg-emerald-500/[0.03] leading-relaxed">
                {r.us}
              </div>
            </div>
          ))}
        </div>

        {/* Founder callback */}
        <div className="mt-8 rounded-xl border border-[#FF5A1F]/40 bg-[#FF5A1F]/5 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-[#FF9764] font-semibold mb-1">
              Skeptical? Good. Test me.
            </div>
            <div className="text-white text-base sm:text-lg leading-snug">
              Call <span className="font-bold text-[#FF5A1F]">(605) 679-7303</span> right now. If a human doesn't answer within 24 hours, you get the whole platform free for 12 months.
            </div>
          </div>
          <Link
            to="/contact?intent=concierge-setup"
            className="btn-primary rounded-full px-5 py-3 text-sm inline-flex items-center gap-2 whitespace-nowrap"
            data-testid="why-us-cta"
          >
            Book a call with Michael
            <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────  LIVE LEADERBOARD WIDGET  ─────────────────────
   Top-5 carriers by referrals this month. Pulls from /leaderboard/carriers
   and hides itself entirely when there's no data yet (fresh launch state)
   so we never show an embarrassing "0 carriers" strip. */
export function LeaderboardWidget() {
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let cancelled = false;
    client
      .get("/leaderboard/carriers", { params: { limit: 5 } })
      .then((r) => { if (!cancelled) setState({ loading: false, data: r.data }); })
      .catch(() => { if (!cancelled) setState({ loading: false, data: null }); });
    return () => { cancelled = true; };
  }, []);

  if (state.loading) return null;
  const leaders = state.data?.leaders || [];
  if (!leaders.length) return null;

  const monthLabel = (() => {
    try {
      const [y, m] = (state.data.month || "").split("-").map(Number);
      if (!y || !m) return "This month";
      return new Date(y, m - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
    } catch { return "This month"; }
  })();

  const totals = state.data?.totals || {};
  const allTime = state.data?.all_time_referrals ?? 0;

  return (
    <section
      aria-label="Referral leaderboard"
      data-testid="home-leaderboard-widget"
      className="border-b border-white/10 bg-black/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14 sm:py-20">
        <div className="grid gap-8 md:grid-cols-[1.25fr_1fr] md:items-end">
          <div>
            <div className="overline mb-3 text-[#FF9764]">Real carriers, real momentum</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[42px] font-semibold tracking-tight leading-tight">
              This month's top{" "}
              <span className="text-[#FF5A1F]">carrier-to-carrier</span> referrers.
            </h2>
            <p className="text-neutral-300 text-base leading-relaxed mt-3 max-w-xl">
              Every carrier on this list invited another small-fleet owner to FreightPilotAI —
              and each got <b className="text-white">30 free days</b> for helping the community.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-5">
              <Link
                to="/leaderboard/carriers"
                data-testid="home-leaderboard-see-all"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#FF5A1F] text-black text-sm font-bold px-4 py-2 hover:bg-[#FF7038] transition-colors"
              >
                See the full leaderboard
                <ArrowRight size={14} weight="bold" />
              </Link>
              <Link
                to="/refer-earn"
                data-testid="home-leaderboard-refer"
                className="inline-flex items-center gap-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white text-sm font-semibold px-4 py-2 border border-white/10 transition-colors"
              >
                Get my referral link
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-6 text-[13px] text-neutral-400">
              <div className="flex items-center gap-1.5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                </span>
                <b className="text-white">{totals.referrals ?? 0}</b> referrals in {monthLabel}
              </div>
              <div>
                <b className="text-white">{allTime.toLocaleString()}</b> lifetime carrier-to-carrier signups
              </div>
              <div>
                <b className="text-white">{(totals.days_awarded ?? 0).toLocaleString()}</b> free days awarded this month
              </div>
            </div>
          </div>

          <div
            data-testid="home-leaderboard-list"
            className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm p-5 sm:p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs uppercase tracking-widest text-[#FF9764] font-bold">
                Top 5 · {monthLabel}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">
                Live
              </div>
            </div>
            <ol className="space-y-2">
              {leaders.map((row, idx) => (
                <li
                  key={idx}
                  data-testid={`home-leaderboard-row-${row.rank}`}
                  className="flex items-center gap-3 rounded-lg bg-black/40 border border-white/5 px-3 py-2.5 hover:border-white/15 transition-colors"
                >
                  <MedalTile rank={row.rank} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white truncate">
                      {row.display_name}
                      {row.state && (
                        <span className="ml-1.5 text-[10px] uppercase tracking-widest text-white/40 font-bold">
                          {row.state}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-white/50 truncate">
                      {row.referrals} referral{row.referrals === 1 ? "" : "s"} · +{row.reward_days} days earned
                      {row.used_careers_page && <span className="ml-1.5 text-emerald-300">· via careers page</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
            <Link
              to="/leaderboard/carriers"
              data-testid="home-leaderboard-widget-link"
              className="mt-4 inline-flex items-center gap-1 text-xs text-[#FF5A1F] hover:text-[#FF7038] font-medium"
            >
              See the full leaderboard →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export function MedalTile({ rank }) {
  const medals = { 1: "🥇", 2: "🥈", 3: "🥉" };
  const bg = rank === 1
    ? "bg-gradient-to-br from-amber-400/30 to-amber-500/10 border-amber-400/40"
    : rank === 2
      ? "bg-gradient-to-br from-slate-300/30 to-slate-400/10 border-slate-300/30"
      : rank === 3
        ? "bg-gradient-to-br from-orange-400/30 to-orange-500/10 border-orange-400/40"
        : "bg-white/5 border-white/10";
  return (
    <div className={`shrink-0 w-8 h-8 rounded-lg border flex items-center justify-center text-sm font-bold ${bg}`}>
      {medals[rank] || <span className="text-white/70 text-[13px] font-mono">{rank}</span>}
    </div>
  );
}


/* Compact, above-the-fold pricing transparency — placed immediately after the
   hero so visitors don't have to scroll five sections to learn what it costs.
   Numbers here MUST stay in sync with the PRICING_TIERS constant below. */

/* ─────────────────────  TIKTOK REELS WIDGET  ─────────────────────
   3-thumbnail preview of the latest FreightPilotAI TikToks with a
   "Watch all" CTA linking to /tiktoks. Auto-hides when there's
   nothing to show so the homepage never has an empty strip. */
export function TikToksWidget() {
  const [state, setState] = useState({ loading: true, posts: [] });
  useEffect(() => {
    let cancelled = false;
    client
      .get("/tiktoks/featured", { params: { limit: 3 } })
      .then((r) => { if (!cancelled) setState({ loading: false, posts: r.data?.posts || [] }); })
      .catch(() => { if (!cancelled) setState({ loading: false, posts: [] }); });
    return () => { cancelled = true; };
  }, []);
  if (state.loading) return null;
  if (state.posts.length === 0) return null;

  return (
    <section
      aria-label="Latest TikTok reels"
      data-testid="home-tiktoks-widget"
      className="border-b border-white/10 bg-black/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-14 sm:py-20">
        <div className="grid gap-8 md:grid-cols-[1.1fr_1.4fr] md:items-center">
          <div>
            <div className="overline mb-3 text-[#FF9764]">Real receipts · every week</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[42px] font-semibold tracking-tight leading-tight">
              Real{" "}
              <span className="text-[#FF5A1F]">owner-op stories</span> on TikTok.
            </h2>
            <p className="text-neutral-300 text-base leading-relaxed mt-3 max-w-xl">
              Weekly videos on running a small fleet without losing your mind — dispatch tricks, invoicing wins, and the real story behind FreightPilotAI.
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-5">
              <Link
                to="/tiktoks"
                data-testid="home-tiktoks-see-all"
                className="inline-flex items-center gap-1.5 rounded-md bg-[#FF5A1F] text-black text-sm font-bold px-4 py-2 hover:bg-[#FF7038] transition-colors"
              >
                Watch all our TikToks
                <ArrowRight size={14} weight="bold" />
              </Link>
              <a
                href="https://www.tiktok.com/@freightpilotai"
                target="_blank"
                rel="noreferrer"
                data-testid="home-tiktoks-follow"
                className="inline-flex items-center gap-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white text-sm font-semibold px-4 py-2 border border-white/10 transition-colors"
              >
                Follow @freightpilotai
              </a>
            </div>
          </div>

          <div
            data-testid="home-tiktoks-thumbs"
            className="grid grid-cols-3 gap-3"
          >
            {state.posts.slice(0, 3).map((p) => (
              <a
                key={p.id}
                href={p.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`home-tiktoks-thumb-${p.id}`}
                className="group relative rounded-xl overflow-hidden border border-white/10 hover:border-[#FF5A1F]/60 transition-colors bg-black/40 block"
                style={{ aspectRatio: "9/16" }}
              >
                {p.thumbnail_url ? (
                  <img
                    src={p.thumbnail_url}
                    alt={p.caption || p.title || "TikTok thumbnail"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/25 text-[10px] uppercase tracking-widest">
                    No preview
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/90 to-transparent">
                  <div className="text-white text-[11px] font-bold leading-tight line-clamp-2">
                    {p.caption || p.title || "Watch on TikTok"}
                  </div>
                </div>
                <div
                  aria-hidden
                  className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40"
                >
                  <div className="w-14 h-14 rounded-full bg-white/95 text-black flex items-center justify-center text-lg font-black">▶</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

